// src/pages/institution-admin/InstitutionTopicView.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    InputAdornment,
    Tooltip,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Fade,
    Avatar,
    Stack,
    Badge,
    Autocomplete,
    Card,
    CardContent,
    LinearProgress,
    Tabs,
    Tab
} from '@mui/material';
import {
    Search,
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    FilterList as FilterIcon,
    Today as TodayIcon,
    Send as SendIcon,
    CheckCircle,
    Cancel,
    Schedule,
    Psychology,
    MenuBook,
    Science,
    QuestionAnswer,
    Flag,
    Feedback,
    Article,
    Description,
    LocalOffer,
    Lightbulb,
    DateRange,
    ExpandMore,
    ClearAll,
    PictureAsPdf,
    Download,
    SupervisorAccount,
    PersonAdd,
    Work,
    School,
    Email,
    Phone,
    Person,
    Assignment as AssignmentIcon,
    Verified,
    Pending,
    RateReview,
    Close,
    Category,
    TaskAlt,
    Update
} from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIResearchTopicByUniversity,
    APIResearchTopicUpdateStatus,
    APIResearchTopicMarkCompleted,
    APIResearchTopicSupervisorsBySpecialty,
    APIAssignStudentStore,
    APIAssignStudentShow,
    APISupervisorProfileImage,
    APIStudentProfileImage,
    APIAssignStudentUpdateReAssign
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails,
    getImageUrl
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
// NOTE: The component file is still named InstitutionAdminBar.js
// but it now renders "Unit Research Lead Portal" as its visible title.
import InstitutionAdminBar from '../../components/InstitutionAdminBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

// Helper function to parse JSON fields
const parseJsonField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    
    try {
        let parsed = JSON.parse(field);
        while (typeof parsed === 'string') {
            try {
                parsed = JSON.parse(parsed);
            } catch {
                break;
            }
        }
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        try {
            let cleaned = field;
            if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
                cleaned = cleaned.slice(1, -1);
            }
            cleaned = cleaned.replace(/\\"/g, '"');
            cleaned = cleaned.replace(/\\\\/g, '\\');
            const parsed = JSON.parse(cleaned);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e2) {
            console.warn('Failed to parse JSON field:', field, e2);
            return [];
        }
    }
};

const getInitials = (name) => {
    if (!name) return 'N/A';
    return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

const stringToColor = (string) => {
    if (!string) return '#1976d2';
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

// Topic status options
const topicStatusOptions = [
    { id: 'draft', name: 'Draft', color: 'default', icon: <Description /> },
    { id: 'submitted', name: 'Submitted', color: 'info', icon: <SendIcon /> },
    { id: 'under_review', name: 'Under Review', color: 'warning', icon: <Schedule /> },
    { id: 'revision_required', name: 'Revision Required', color: 'warning', icon: <Feedback /> },
    { id: 'approved', name: 'Approved', color: 'success', icon: <CheckCircle /> },
    { id: 'rejected', name: 'Rejected', color: 'error', icon: <Cancel /> },
    { id: 'completed', name: 'Completed', color: 'success', icon: <TaskAlt /> },
    { id: 'superseded', name: 'Superseded', color: 'secondary', icon: <Update /> }
];

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const InstitutionTopicView = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [topics, setTopics] = useState([]);
    const [filteredTopics, setFilteredTopics] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
    const [feedbackInput, setFeedbackInput] = useState('');
    const [completionNotes, setCompletionNotes] = useState('');
    const isMounted = useRef(true);
    const adminId = getUserDetails("Id");
    const universityId = getUserDetails("universityId");

    // Assignment dialog states
    const [openAssignDialog, setOpenAssignDialog] = useState(false);
    const [availableSupervisors, setAvailableSupervisors] = useState([]);
    const [selectedSupervisor, setSelectedSupervisor] = useState(null);
    const [assignmentNotes, setAssignmentNotes] = useState('');
    const [assigning, setAssigning] = useState(false);
    const [existingAssignment, setExistingAssignment] = useState(null);
    const [supervisorSearchInput, setSupervisorSearchInput] = useState('');
    const [loadingSupervisors, setLoadingSupervisors] = useState(false);

    // View dialog states
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [openStatusDialog, setOpenStatusDialog] = useState(false);
    const [openCompleteDialog, setOpenCompleteDialog] = useState(false);

    // Filters
    const [filters, setFilters] = useState({
        topic_status: '',
        dateFrom: '',
        dateTo: ''
    });
    const [filterOptions, setFilterOptions] = useState(false);

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchTopics();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        applyFilters();
    }, [topics, searchTerm, filters, activeTab]);

    const fetchTopics = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const result = await apiService.listById(APIResearchTopicByUniversity, universityId);
            if (result.success && isMounted.current) {
                const parsedTopics = (Array.isArray(result.data) ? result.data : []).map(topic => ({
                    ...topic,
                    keywords: parseJsonField(topic.keywords),
                    research_questions: parseJsonField(topic.research_questions),
                    objectives: parseJsonField(topic.objectives)
                })).reverse();
                setTopics(parsedTopics);
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load topics");
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchSupervisorsBySpecialty = async (specialtyId) => {
        setLoadingSupervisors(true);
        try {
            const formData = new FormData();
            formData.append('specialty_id', specialtyId);
            formData.append('university_id', universityId);
            
            const result = await apiService.create(APIResearchTopicSupervisorsBySpecialty, formData);
            
            if (result.success && isMounted.current) {
                setAvailableSupervisors(Array.isArray(result.data) ? result.data : []);
                if (result.data?.length === 0) {
                    showToast("Info", "No supervisors available for this specialty", "info");
                }
            } else {
                setAvailableSupervisors([]);
                showToast("Error", result.message || "Failed to load supervisors", "error");
            }
        } catch (error) {
            console.error("Failed to fetch supervisors:", error);
            setAvailableSupervisors([]);
            showToast("Error", "Failed to load supervisors for this specialty", "error");
        } finally {
            setLoadingSupervisors(false);
        }
    };

    const fetchTopicAssignment = async (topicId) => {
        try {
            const result = await apiService.listById(APIAssignStudentShow, topicId);
            if (result.success && result.data) {
                setExistingAssignment(result.data);
            } else {
                setExistingAssignment(null);
            }
        } catch (error) {
            setExistingAssignment(null);
        }
    };

    const applyFilters = () => {
        let filtered = [...topics];
        
        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(topic =>
                topic.topic_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                topic.topic_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                topic.student_name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        // Status filter
        if (filters.topic_status) {
            filtered = filtered.filter(topic => topic.topic_status === filters.topic_status);
        }
        
        // Date filter
        if (filters.dateFrom) {
            filtered = filtered.filter(topic => 
                topic.submitted_date && topic.submitted_date.split('T')[0] >= filters.dateFrom
            );
        }
        if (filters.dateTo) {
            filtered = filtered.filter(topic => 
                topic.submitted_date && topic.submitted_date.split('T')[0] <= filters.dateTo
            );
        }
        
        // Tab filter
        if (activeTab === 1) {
            filtered = filtered.filter(topic => !topic.supervisor_id);
        } else if (activeTab === 2) {
            filtered = filtered.filter(topic => topic.supervisor_id);
        } else if (activeTab === 3) {
            filtered = filtered.filter(topic => topic.topic_status === 'completed');
        }
        
        setFilteredTopics(filtered);
        setPage(0);
    };

    const handleOpenAssignDialog = async (topic) => {
        setSelectedTopic(topic);
        setSelectedSupervisor(null);
        setAssignmentNotes('');
        setSupervisorSearchInput('');
        setAvailableSupervisors([]);
        
        await fetchTopicAssignment(topic.id);
        
        // Fetch supervisors based on topic specialty
        if (topic.specialty_id) {
            await fetchSupervisorsBySpecialty(topic.specialty_id);
        } else {
            showToast("Warning", "This topic has no specialty assigned. Please update the topic first.", "warning");
        }
        
        setOpenAssignDialog(true);
    };

    const handleCloseAssignDialog = () => {
        setOpenAssignDialog(false);
        setSelectedTopic(null);
        setSelectedSupervisor(null);
        setAssignmentNotes('');
        setExistingAssignment(null);
        setAvailableSupervisors([]);
    };

    const handleAssignSupervisor = async () => {
        if (!selectedSupervisor) {
            showToast("Error", "Please select a supervisor", "error");
            return;
        }
        setAssigning(true);
        try {
            const formData = new FormData();
            formData.append('student_id', selectedTopic.student_id);
            formData.append('supervisor_id', selectedSupervisor.id);
            formData.append('assigned_by', adminId);
            formData.append('topic_id', selectedTopic.id);
            if (assignmentNotes) {
                formData.append('assignment_notes', assignmentNotes);
            }
            
            const result = await apiService.create(APIAssignStudentStore, formData);
            
            if (result && result.success) {
                showToast("Success", `Supervisor assigned successfully to topic: ${selectedTopic.topic_title}`, "success");
                handleCloseAssignDialog();
                fetchTopics();
            } else {
                showToast("Error", result.message || "Failed to assign supervisor", "error");
            }
        } catch (error) {
            showToast("Error", error.response?.data?.message || "Failed to assign supervisor", "error");
        } finally {
            setAssigning(false);
        }
    };

    const handleUpdateAssignment = async () => {
        if (!selectedSupervisor) {
            showToast("Error", "Please select a supervisor", "error");
            return;
        }
        setAssigning(true);
        try {
            const formData = new FormData();
            formData.append('updateId', selectedTopic.student_id);
            formData.append('student_id', selectedTopic.student_id);
            formData.append('supervisor_id', selectedSupervisor.id);
            formData.append('topic_id', selectedTopic.id);
            if (assignmentNotes) {
                formData.append('assignment_notes', assignmentNotes);
            }
            
            const result = await apiService.update(APIAssignStudentUpdateReAssign, formData);
            
            if (result && result.success) {
                showToast("Success", `Supervisor reassigned successfully for topic: ${selectedTopic.topic_title}`, "success");
                handleCloseAssignDialog();
                fetchTopics();
            } else {
                showToast("Error", result.message || "Failed to update assignment", "error");
            }
        } catch (error) {
            showToast("Error", error.response?.data?.message || "Failed to update assignment", "error");
        } finally {
            setAssigning(false);
        }
    };

    const handleOpenViewDialog = (topic) => {
        setSelectedTopic(topic);
        setOpenViewDialog(true);
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setSelectedTopic(null);
    };

    const handleOpenStatusDialog = (topic) => {
        setSelectedTopic(topic);
        setFeedbackInput(topic.supervisor_feedback || '');
        setOpenStatusDialog(true);
    };

    const handleCloseStatusDialog = () => {
        setOpenStatusDialog(false);
        setSelectedTopic(null);
        setFeedbackInput('');
    };

    // Open Complete Dialog
    const handleOpenCompleteDialog = (topic) => {
        setSelectedTopic(topic);
        setCompletionNotes('');
        setOpenCompleteDialog(true);
    };

    // Close Complete Dialog
    const handleCloseCompleteDialog = () => {
        setOpenCompleteDialog(false);
        setSelectedTopic(null);
        setCompletionNotes('');
    };

    const handleUpdateStatus = async (newStatus) => {
        setStatusUpdateLoading(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', adminId);
            formDataToSend.append('updateId', selectedTopic.id);
            formDataToSend.append('topic_status', newStatus);
            if (feedbackInput) {
                formDataToSend.append('supervisor_feedback', feedbackInput);
            }

            const result = await apiService.update(APIResearchTopicUpdateStatus, formDataToSend);

            if (result && result.success && isMounted.current) {
                const statusName = topicStatusOptions.find(s => s.id === newStatus)?.name || newStatus;
                showToast("Success", `Topic status updated to ${statusName}`, "success");
                fetchTopics();
                handleCloseStatusDialog();
            }
        } catch (error) {
            showToast("Error", error.response?.data?.message || "Failed to update status", "error");
        } finally {
            setStatusUpdateLoading(false);
        }
    };

    // Handle Mark as Completed
    const handleCompletedResearch = async (topicId, newStatus, feedback = null, additionalNotes = null) => {
        setStatusUpdateLoading(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('updateId', topicId);
            formDataToSend.append('topic_status', newStatus);

            if (feedback) {
                formDataToSend.append('supervisor_feedback', feedback);
            }
            if (additionalNotes) {
                formDataToSend.append('completion_notes', additionalNotes);
            }

            const result = await apiService.update(APIResearchTopicMarkCompleted, formDataToSend);

            if (result && result.success && isMounted.current) {
                const statusName = topicStatusOptions.find(s => s.id === newStatus)?.name || newStatus;
                showToast("Success", `Topic ${statusName.toLowerCase()} successfully`, "success");
                fetchTopics();
                handleCloseCompleteDialog();
                handleCloseStatusDialog();
            }
        } catch (error) {
            showToast("Error", error.response?.data?.message || "Failed to update status", "error");
        } finally {
            setStatusUpdateLoading(false);
        }
    };

    const handleDownloadFile = (fileUrl) => {
        if (fileUrl) {
            window.open(fileUrl, '_blank');
        }
    };

    const getStatusChip = (status) => {
        const option = topicStatusOptions.find(s => s.id === status);
        return (
            <Chip
                icon={option?.icon}
                label={option?.name || status}
                color={option?.color}
                size="small"
            />
        );
    };

    const getSupervisorAvatar = (supervisor) => {
        if (!supervisor) return null;
        const imageUrl = supervisor.profile_image ? 
            getImageUrl(APISupervisorProfileImage, supervisor.profile_image) : null;
        const initials = getInitials(supervisor.name);
        const bgColor = stringToColor(supervisor.name);
        
        return (
            <Avatar 
                src={imageUrl}
                sx={{ 
                    width: 40, 
                    height: 40,
                    bgcolor: !imageUrl ? bgColor : 'transparent',
                    fontSize: '1rem'
                }}
            >
                {!imageUrl && initials}
            </Avatar>
        );
    };

    const getStudentAvatar = (student) => {
        if (!student) return null;
        const imageUrl = student.profile_image ? 
            getImageUrl(APIStudentProfileImage, student.profile_image) : null;
        const initials = getInitials(student.name);
        const bgColor = stringToColor(student.name);
        
        return (
            <Avatar 
                src={imageUrl}
                sx={{ 
                    width: 40, 
                    height: 40,
                    bgcolor: !imageUrl ? bgColor : 'transparent',
                    fontSize: '1rem'
                }}
            >
                {!imageUrl && initials}
            </Avatar>
        );
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters(prev => ({ ...prev, dateFrom: today, dateTo: today }));
        setFilterOptions(false);
    };

    const clearFilters = () => {
        setFilters({
            topic_status: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        setActiveTab(0);
        setFilterOptions(false);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (loading) {
        return <LoadingState message="Loading Research Topics..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchTopics} title="Unable to Load Research Topics" variant="research" fullPage retryText="Retry Loading" />;
    }

    const tabLabels = [
        { label: 'All Topics', count: topics.length, icon: <Article /> },
        { label: 'Unassigned', count: topics.filter(t => !t.supervisor_id).length, icon: <Pending /> },
        { label: 'Assigned', count: topics.filter(t => t.supervisor_id).length, icon: <Verified /> },
        { label: 'Completed', count: topics.filter(t => t.topic_status === 'completed').length, icon: <TaskAlt /> }
    ];

    return (
        <FullWidthContainer >
            <Box sx={{ backgroundColor: 'white', borderRadius: 3 }}>
                <InstitutionAdminBar notificationCount={5} />
                
                {/* Header Section */}
                <Paper elevation={2} sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 2, sm: 3 }, 
                    borderRadius: 3,
                    backgroundColor: 'white',
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        flexWrap: 'wrap',
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Typography variant="h6" sx={{
                            fontWeight: 'bold',
                            color: theme.palette.primary.main,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}>
                            <SupervisorAccount /> Research Topics Management
                        </Typography>

                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            flexShrink: 0,
                            width: { xs: '100%', sm: 'auto' },
                            justifyContent: { xs: 'center', sm: 'flex-start' }
                        }}>
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={() => setFilterOptions(true)}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                View Options
                            </Button>
                            <Button
                                variant="outlined"
                                color="info"
                                startIcon={<RefreshIcon />}
                                onClick={fetchTopics}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Refresh
                            </Button>
                        </Box>

                        {/* Search Bar */}
                        <Paper sx={{
                            width: { xs: '100%', sm: '300px' },
                            p: '2px 8px',
                            display: 'flex',
                            alignItems: 'center',
                            marginLeft: { xs: 0, sm: 'auto' },
                            borderRadius: '8px',
                            backgroundColor: theme.palette.grey[50],
                        }}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Search by title, ID, or student..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search sx={{ color: theme.palette.grey[600] }} />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        '& fieldset': { border: 'none' },
                                    }
                                }}
                                size="small"
                            />
                        </Paper>
                    </Box>

                    {/* Active Filters */}
                    {(filters.topic_status || filters.dateFrom || filters.dateTo) && (
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            mt: 2,
                            pt: 2,
                            borderTop: '1px solid',
                            borderColor: 'divider'
                        }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                Active filters:
                            </Typography>
                            {filters.topic_status && (
                                <Chip
                                    label={`Status: ${topicStatusOptions.find(s => s.id === filters.topic_status)?.name}`}
                                    onDelete={() => setFilters(prev => ({ ...prev, topic_status: '' }))}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            {filters.dateFrom && (
                                <Chip
                                    label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                    onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            {filters.dateTo && (
                                <Chip
                                    label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                    onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            <Button size="small" onClick={clearFilters} startIcon={<ClearAll />}>
                                Clear All
                            </Button>
                        </Box>
                    )}
                </Paper>

                {/* Tabs */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    mb: { xs: 2, sm: 3 }
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider', 
                            px: { xs: 1, sm: 2 }, 
                            pt: 2,
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: { xs: 'auto', sm: 120 },
                                px: { xs: 1, sm: 2 }
                            }
                        }}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        {tabLabels.map(tab => (
                            <Tab 
                                key={tab.label}
                                icon={tab.icon}
                                label={
                                    <Badge badgeContent={tab.count} color="primary" sx={{ mr: 1 }}>
                                        {tab.label}
                                    </Badge>
                                }
                            />
                        ))}
                    </Tabs>
                </Paper>
            </Box>

            {/* Topics Table */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'auto',
                    backgroundColor: 'white',
                }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: { xs: 900, sm: 1000, md: 1200 } }}>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' }
                                    }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Student</TableCell>
                                    <TableCell>Title</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Supervisor</TableCell>
                                    <TableCell>Submitted</TableCell>
                                    <TableCell>Assigned</TableCell>
                                    <TableCell >Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredTopics.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((topic, index) => (
                                    <TableRow
                                        key={topic.id}
                                        hover
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: theme.palette.action.hover
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <Chip label={page * rowsPerPage + index + 1} color="primary" size="small" />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                {getStudentAvatar({ name: topic.student_name, profile_image: topic.student_profile_image })}
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {topic.student_name || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Article fontSize="small" color="primary" />
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {topic.topic_title?.substring(0, 60)}...
                                                </Typography>
                                            </Box>
                                            {topic.file_url && (
                                                <Tooltip title="Download PDF">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => handleDownloadFile(topic.file_url)}
                                                        sx={{ mt: 0.5 }}
                                                    >
                                                        <PictureAsPdf fontSize="small" color="error" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                
                                        <TableCell>
                                            {getStatusChip(topic.topic_status)}
                                        </TableCell>
                                        <TableCell>
                                            {topic.supervisor_name ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {getSupervisorAvatar({ 
                                                        name: topic.supervisor_name, 
                                                        profile_image: topic.supervisor_profile_image 
                                                    })}
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {topic.supervisor_name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {topic.supervisor_academic_title}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Chip 
                                                    label="Not Assigned" 
                                                    size="small" 
                                                    color="warning" 
                                                    variant="outlined"
                                                    icon={<Pending />}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>{formatCreatedAtDate(topic.submitted_date)}</TableCell>
                                        <TableCell>{topic.assigned_date}</TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                                                <Tooltip title="View Details">
                                                    <IconButton onClick={() => handleOpenViewDialog(topic)} color="primary" size="small">
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                {topic.topic_status === "draft" ? (
                                                    <Chip label="In Draft" color="info" size="small" variant="outlined" />
                                                ) : topic.topic_status === "completed" ? (
                                                    <Chip 
                                                        label="Completed" 
                                                        color="success" 
                                                        size="small" 
                                                        icon={<TaskAlt />} 
                                                    />
                                                ) : (
                                                    <>
                                                        {topic.topic_status !== 'approved' && topic.topic_status !== 'rejected' && (
                                                            <Tooltip title={topic.supervisor_id ? "Reassign Supervisor" : "Assign Supervisor"}>
                                                                <IconButton 
                                                                    onClick={() => handleOpenAssignDialog(topic)} 
                                                                    color="secondary" 
                                                                    size="small"
                                                                    disabled={!topic.specialty_id}
                                                                >
                                                                    {topic.supervisor_id ? <RefreshIcon /> : <PersonAdd />}
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        {topic.topic_status !== 'approved' && topic.topic_status !== 'rejected' && (
                                                            <Tooltip title="Update Status">
                                                                <IconButton onClick={() => handleOpenStatusDialog(topic)} color="warning" size="small">
                                                                    <Schedule />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        {/* Mark as Completed button for approved topics */}
                                                        {topic.topic_status === 'approved' && (
                                                            <Tooltip title="Mark as Completed">
                                                                <IconButton onClick={() => handleOpenCompleteDialog(topic)} color="success" size="small">
                                                                    <TaskAlt />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={filteredTopics.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ mt: 2 }}
                    />

                    {filteredTopics.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 8,
                            color: 'text.secondary'
                        }}>
                            <Search sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No research topics found</Typography>
                            <Typography variant="body2">Try adjusting your search query or filters</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Filter Options Dialog */}
            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                onClose={() => setFilterOptions(false)}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Topics
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Submissions
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Status
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Topic Status</InputLabel>
                            <Select
                                name="topic_status"
                                value={filters.topic_status}
                                onChange={handleFilterChange}
                                label="Topic Status"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                {topicStatusOptions.map((status) => (
                                    <MenuItem key={status.id} value={status.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {status.icon}
                                            {status.name}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setFilterOptions(false)} variant="contained">Close</Button>
                    <Button onClick={() => setFilterOptions(false)} color="primary" variant="contained">Apply Filters</Button>
                </DialogActions>
            </Dialog>

            {/* Assign Supervisor Dialog */}
            <Dialog
                open={openAssignDialog}
                onClose={handleCloseAssignDialog}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <SupervisorAccount color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {existingAssignment ? 'Reassign Supervisor' : 'Assign Supervisor to Topic'}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedTopic && (
                        <Box sx={{ pt: 2 }}>
                            {/* Topic Information */}
                            <Paper sx={{ p: 2, mb: 3, bgcolor: theme.palette.grey[50], borderRadius: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.primary.main }}>
                                    Topic Information
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Topic ID:</strong> {selectedTopic.topic_id}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Title:</strong> {selectedTopic.topic_title}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Student:</strong> {selectedTopic.student_name} ({selectedTopic.student_registration_id})
                                </Typography>
                                {selectedTopic.specialty_name && (
                                    <Typography variant="body2">
                                        <strong>Required Specialty:</strong> 
                                        <Chip 
                                            icon={<Category />}
                                            label={selectedTopic.specialty_name}
                                            size="small"
                                            color="primary"
                                            sx={{ ml: 1 }}
                                        />
                                    </Typography>
                                )}
                            </Paper>

                            {/* Current Assignment Info */}
                            {existingAssignment && (
                                <Paper sx={{ p: 2, mb: 3, bgcolor: theme.palette.warning.light + '20', borderRadius: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.warning.main }}>
                                        Current Assignment
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        <strong>Current Supervisor:</strong> {existingAssignment.supervisor?.name}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Assigned Date:</strong> {formatCreatedAtDate(existingAssignment.assigned_date)}
                                    </Typography>
                                </Paper>
                            )}

                            {/* Supervisor Selection */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.primary.main }}>
                                Select Supervisor (Must have expertise in: {selectedTopic.specialty_name}) *
                            </Typography>
                            
                            {loadingSupervisors ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : availableSupervisors.length === 0 ? (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    No supervisors available for this specialty. Please add supervisors with expertise in {selectedTopic.specialty_name}.
                                </Alert>
                            ) : (
                                <Autocomplete
                                    options={availableSupervisors}
                                    getOptionLabel={(option) => `${option.name} (${option.academic_title})`}
                                    value={selectedSupervisor}
                                    onChange={(event, newValue) => setSelectedSupervisor(newValue)}
                                    inputValue={supervisorSearchInput}
                                    onInputChange={(event, newInputValue) => setSupervisorSearchInput(newInputValue)}
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                                <Avatar 
                                                    src={getImageUrl(APISupervisorProfileImage, option.profile_image)}
                                                    sx={{ width: 40, height: 40 }}
                                                >
                                                    {!option.profile_image && getInitials(option.name)}
                                                </Avatar>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {option.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {option.academic_title}
                                                    </Typography>
                                                    {option.research_expertise && (
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            Expertise: {option.research_expertise.substring(0, 100)}...
                                                        </Typography>
                                                    )}
                                                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                                        <Typography variant="caption" color="primary">
                                                            Current Students: {option.current_students || 0}
                                                        </Typography>
                                                        {option.years_of_experience && (
                                                            <Typography variant="caption" color="secondary">
                                                                Experience: {option.years_of_experience} years
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                                {option.is_primary_expert && (
                                                    <Chip label="Primary Expert" size="small" color="success" />
                                                )}
                                            </Box>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Search Supervisor"
                                            placeholder="Type to search supervisors..."
                                            required
                                            error={!selectedSupervisor}
                                            helperText={!selectedSupervisor ? "Please select a supervisor with expertise in this specialty" : ""}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Work color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ mb: 3 }}
                                        />
                                    )}
                                />
                            )}

                            {/* Assignment Notes */}
                            <TextField
                                fullWidth
                                label="Assignment Notes (Optional)"
                                multiline
                                rows={4}
                                value={assignmentNotes}
                                onChange={(e) => setAssignmentNotes(e.target.value)}
                                placeholder="Add notes about this assignment..."
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <RateReview color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, gap: 2 }}>
                    <Button onClick={handleCloseAssignDialog} variant="outlined" disabled={assigning}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={existingAssignment ? handleUpdateAssignment : handleAssignSupervisor} 
                        variant="contained" 
                        color="primary"
                        disabled={assigning || !selectedSupervisor || loadingSupervisors || availableSupervisors.length === 0}
                        startIcon={assigning ? <CircularProgress size={20} /> : <PersonAdd />}
                    >
                        {assigning ? 'Processing...' : (existingAssignment ? 'Reassign Supervisor' : 'Assign Supervisor')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Details Dialog */}
            <Dialog
                open={openViewDialog}
                onClose={handleCloseViewDialog}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
                scroll="paper"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Research Topic Details</b>
                </DialogTitle>
                <DialogContent>
                    {selectedTopic && (
                        <Box sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h6" color="primary">
                                    {selectedTopic.topic_id}
                                </Typography>
                                <Box>
                                    {getStatusChip(selectedTopic.topic_status)}
                                </Box>
                            </Box>

                            <Accordion defaultExpanded>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Lightbulb color="primary" />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                            Topic Information
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <b>Student:</b> {selectedTopic.student_name} ({selectedTopic.student_registration_id})
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <b>Title:</b> {selectedTopic.topic_title}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <b>Description:</b> {selectedTopic.topic_description}
                                            </Typography>
                                        </Grid>
                                        {selectedTopic.specialty_name && (
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <b>Research Specialty:</b> 
                                                    <Chip 
                                                        icon={<Category />}
                                                        label={selectedTopic.specialty_name}
                                                        size="small"
                                                        sx={{ ml: 1 }}
                                                    />
                                                </Typography>
                                            </Grid>
                                        )}
                                        {selectedTopic.supervisor_name && (
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <b>Supervisor:</b> {selectedTopic.supervisor_name}
                                                </Typography>
                                            </Grid>
                                        )}
                                        {selectedTopic.file_url && (
                                            <Grid item xs={12}>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<PictureAsPdf />}
                                                    onClick={() => handleDownloadFile(selectedTopic.file_url)}
                                                    size="small"
                                                >
                                                    Download Research Document
                                                </Button>
                                            </Grid>
                                        )}
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Science color="secondary" />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                            Research Details
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <b>Keywords:</b>
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                                {selectedTopic.keywords?.map((k, i) => (
                                                    <Chip key={i} label={k} size="small" />
                                                ))}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <b>Research Questions:</b>
                                            </Typography>
                                            <ul>
                                                {selectedTopic.research_questions?.map((q, i) => (
                                                    <li key={i}>
                                                        <Typography variant="body2">{q}</Typography>
                                                    </li>
                                                ))}
                                            </ul>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <b>Objectives:</b>
                                            </Typography>
                                            <ul>
                                                {selectedTopic.objectives?.map((o, i) => (
                                                    <li key={i}>
                                                        <Typography variant="body2">{o}</Typography>
                                                    </li>
                                                ))}
                                            </ul>
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <MenuBook color="info" />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                            Methodology & Contribution
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <b>Methodology:</b>
                                            </Typography>
                                            <Typography variant="body2">
                                                {selectedTopic.methodology_brief || 'Not provided'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <b>Literature Review:</b>
                                            </Typography>
                                            <Typography variant="body2">
                                                {selectedTopic.literature_review_summary || 'Not provided'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <b>Expected Contribution:</b>
                                            </Typography>
                                            <Typography variant="body2">
                                                {selectedTopic.expected_contribution || 'Not provided'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>

                            {selectedTopic.supervisor_feedback && (
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Feedback color="warning" />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                                Supervisor Feedback
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography variant="body2">{selectedTopic.supervisor_feedback}</Typography>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <DateRange color="action" />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                            Dates
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2">
                                                <b>Submitted:</b> {formatCreatedAtDate(selectedTopic.submitted_date)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2">
                                                <b>Last Modified:</b> {formatCreatedAtDate(selectedTopic.last_modified_date)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseViewDialog} variant="contained">Close</Button>
                </DialogActions>
            </Dialog>

            {/* Update Status Dialog */}
            <Dialog
                open={openStatusDialog}
                onClose={handleCloseStatusDialog}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Update Topic Status</b>
                </DialogTitle>
                <DialogContent>
                    {selectedTopic && (
                        <Box sx={{ p: 2 }}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Topic:</strong> {selectedTopic.topic_title}<br/>
                                    <strong>Current Status:</strong> {getStatusChip(selectedTopic.topic_status)}
                                </Typography>
                            </Alert>

                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 2 }}>
                                Select New Status
                            </Typography>

                            <Grid container spacing={2}>
                                {topicStatusOptions
                                    .filter(s => s.id !== 'draft' && s.id !== 'completed' && s.id !== 'superseded')
                                    .map((status) => (
                                        <Grid item xs={12} sm={6} key={status.id}>
                                            <Button
                                                fullWidth
                                                variant={selectedTopic.topic_status === status.id ? "contained" : "outlined"}
                                                color={status.color}
                                                onClick={() => handleUpdateStatus(status.id)}
                                                disabled={statusUpdateLoading || selectedTopic.topic_status === status.id}
                                                sx={{
                                                    justifyContent: 'center',
                                                    textTransform: 'none',
                                                    py: 1.5
                                                }}
                                            >
                                                {status.icon}
                                                <Box sx={{ ml: 1 }}>{status.name}</Box>
                                            </Button>
                                        </Grid>
                                    ))}
                            </Grid>

                            {(selectedTopic.topic_status === 'revision_required' || selectedTopic.topic_status === 'rejected') && (
                                <Box sx={{ mt: 3 }}>
                                    <TextField
                                        fullWidth
                                        label="Feedback to Student"
                                        multiline
                                        rows={3}
                                        value={feedbackInput}
                                        onChange={(e) => setFeedbackInput(e.target.value)}
                                        placeholder="Provide feedback to the student..."
                                    />
                                </Box>
                            )}

                            {statusUpdateLoading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseStatusDialog} variant="contained" disabled={statusUpdateLoading}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Complete Research Dialog */}
            <Dialog
                open={openCompleteDialog}
                onClose={handleCloseCompleteDialog}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ bgcolor: theme.palette.success.main, color: 'white', textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <TaskAlt /> 
                        <b>Complete Research Topic</b>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedTopic && (
                        <Box sx={{ pt: 2 }}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Topic:</strong> {selectedTopic.topic_title}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Student:</strong> {selectedTopic.student_name}
                                </Typography>
                                {selectedTopic.specialty_name && (
                                    <Typography variant="body2">
                                        <strong>Specialty:</strong> {selectedTopic.specialty_name}
                                    </Typography>
                                )}
                            </Alert>
                            <TextField
                                fullWidth
                                label="Completion Notes (Optional)"
                                multiline
                                rows={4}
                                value={completionNotes}
                                onChange={(e) => setCompletionNotes(e.target.value)}
                                placeholder="Add completion remarks or final notes..."
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleCloseCompleteDialog} variant="outlined" disabled={statusUpdateLoading}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={() => handleCompletedResearch(selectedTopic?.id, 'completed', completionNotes)}
                        variant="contained" 
                        color="success"
                        disabled={statusUpdateLoading}
                        startIcon={statusUpdateLoading ? <CircularProgress size={20} /> : <TaskAlt />}
                    >
                        {statusUpdateLoading ? 'Processing...' : 'Mark as Completed'}
                    </Button>
                </DialogActions>
            </Dialog>
        </FullWidthContainer>
    );
};

export default InstitutionTopicView;